import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Panel, WindowContent, toast } from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { ResponsiblePersonsPanel } from '../../components/ResponsiblePersonsPanel';
import { serieFormAgenda } from './serieFormAgenda';
import { SerieFormFields } from './components/SerieFormFields';
import { useSerieForm } from './useSerieForm';

interface SerieFormPageProps {
  layerId: string;
}

/**
 * Formulář pro založení nové série pod produktem (Název série, Typ dopravy, Odpovědné osoby, Poznámka).
 */
export function SerieFormPage({ layerId }: SerieFormPageProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { open } = useAgendaParams(serieFormAgenda, layerId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form } = useSerieForm({
    productId: open.productId,
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('serie.toast.saved') });
      closeForm();
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('serie.toast.errorTitle'),
        description: t('serie.toast.saveError'),
      });
    },
  });

  return (
    <WindowContent
      as="form"
      width="reading"
      data-testid="serie-form"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <footer className="bg-phx-surface-2 border-phx-border-2 px-phx-5 gap-phx-2 py-phx-4 flex shrink-0 items-center justify-end border-t">
              <Button variant="ghost" size="sm" type="button" onClick={closeForm}>
                {t('serie.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit || isSubmitting}
                data-testid="serie-save-button"
              >
                {t('serie.actions.save')}
              </Button>
            </footer>
          )}
        </form.Subscribe>
      }
    >
      <Panel variant="filled" title={t('serie.titleCreate')}>
        <SerieFormFields form={form} />
      </Panel>

      <ResponsiblePersonsPanel
        form={form}
        title={t('serie.persons.sectionLabel')}
        help={t('serie.persons.help')}
        placeholder={t('serie.persons.placeholder')}
        testIdPrefix="serie"
      />
    </WindowContent>
  );
}
